"use client";

import { useState, useEffect } from "react";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { ItemGallery } from "./ItemGallery";
import { PurchaseCTA } from "./PurchaseCTA";
import { StatusTimeline } from "./StatusTimeline";
import { LogisticsQuoteModal } from "../ui/LogisticsQuoteModal";
import { useAuth } from "../../lib/hooks/useAuth";
import { SellItem } from "../../data/types";
import { getUserProfile, getGradeInfo } from "../../lib/profile/api";
import { UserProfile } from "../../data/profile/types";
import { SellerProfileModal } from "../profile/SellerProfileModal";
import { CreateLogisticsOrderInput } from "../../data/types/logistics";
import { EnhancedChatModal } from "../chat/EnhancedChatModal";
import { getOrCreateChat } from "../../lib/chat/api";
import {
  CONDITION_GRADES,
  INSTRUMENT_CATEGORIES,
} from "../../data/constants/index";
import {
  MapPin,
  Calendar,
  Share2,
  X,
  AlertCircle,
  Loader2,
  User,
  Star,
  Shield,
  Truck,
  Trash2,
  Edit,
  ArrowUp,
} from "lucide-react";
import EditProductModal from "../product/EditProductModal";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import toast from "react-hot-toast";

interface ItemDetailModalProps {
  item: SellItem;
  isOpen: boolean;
  onClose: () => void;
  showEditActions?: boolean; // 수정/삭제/끌어올리기 버튼 표시 여부 (기본값: false)
}

export function ItemDetailModal({
  item,
  isOpen,
  onClose,
  showEditActions = false, // 기본값: false (다른 페이지에서는 버튼 숨김)
}: ItemDetailModalProps) {
  const { user } = useAuth();
  const [sellerProfile, setSellerProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [showLogisticsModal, setShowLogisticsModal] = useState(false);
  const [showSellerProfileModal, setShowSellerProfileModal] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBumping, setIsBumping] = useState(false);

  // 판매자인지 확인
  const isSeller = user?.uid === item?.sellerUid || user?.uid === (item as any)?.sellerId;

  // 디버깅: item 객체 확인
  console.log("ItemDetailModal - item 객체:", {
    id: item?.id,
    sellerUid: item?.sellerUid,
    sellerId: (item as any)?.sellerId,
    userUid: user?.uid,
    isSeller,
    brand: item?.brand,
    model: item?.model,
    aiProcessedImages: item?.aiProcessedImages,
  });

  useEffect(() => {
    if (item?.sellerUid) {
      loadSellerProfile();
    }
  }, [item?.sellerUid]);

  const loadSellerProfile = async () => {
    if (!item?.sellerUid) return;

    try {
      setLoading(true);
      const result = await getUserProfile(item.sellerUid);
      if (result.success && result.data) {
        setSellerProfile(result.data);
      }
    } catch (error) {
      console.error("판매자 프로필 로드 실패:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title:
            item?.title ||
            `${item?.brand || ""} ${item?.model || ""}`.trim() ||
            "상품명 없음",
          text: `${categoryInfo?.label || item?.category}`,
          url: window.location.href,
        });
      } catch (err) {
        console.log("공유 취소됨");
      }
    } else {
      // 클립보드에 URL 복사
      await navigator.clipboard.writeText(window.location.href);
      alert("링크가 클립보드에 복사되었습니다!");
    }
  };

  const getConditionInfo = (condition: string) => {
    return CONDITION_GRADES.find(c => c.key === condition);
  };

  const getCategoryInfo = (category: string) => {
    return INSTRUMENT_CATEGORIES.find(c => c.key === category);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("ko-KR").format(price) + "원";
  };

  const formatDate = (date: any) => {
    if (!date) return "";
    try {
      const dateObj = date.toDate ? date.toDate() : new Date(date);
      // 유효한 날짜인지 확인
      if (isNaN(dateObj.getTime())) {
        return "날짜 정보 없음";
      }
      return formatDistanceToNow(dateObj, { addSuffix: true, locale: ko });
    } catch (error) {
      console.error("날짜 포맷팅 오류:", error, date);
      return "날짜 정보 없음";
    }
  };

  const getShippingTypeLabel = (type: string) => {
    switch (type) {
      case "direct":
        return "직거래";
      case "pickup":
        return "픽업";
      case "courier":
      case "parcel": // 기존 데이터 호환성
        return "택배";
      case "meetup": // meetup도 직거래로 처리
        return "직거래";
      case "shipping":
        return "화물운송";
      case "escrow":
        return null; // escrow는 표시하지 않음
      default:
        return type;
    }
  };

  const handleLogisticsQuote = () => {
    if (!user) {
      toast.error("로그인이 필요합니다.");
      return;
    }
    setShowLogisticsModal(true);
  };

  const handleLogisticsOrderCreate = async (
    orderData: CreateLogisticsOrderInput
  ) => {
    try {
      // TODO: 실제로는 logisticsOrders 컬렉션에 문서 생성
      console.log("운송 주문 생성:", orderData);
      toast.success("운송 견적이 확정되었습니다!");
      setShowLogisticsModal(false);
    } catch (error) {
      console.error("운송 주문 생성 실패:", error);
      toast.error("운송 주문 생성에 실패했습니다.");
    }
  };

  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handleDelete = async () => {
    if (!window.confirm("정말로 이 상품을 삭제하시겠습니까?")) {
      return;
    }

    try {
      setIsDeleting(true);
      const { deleteItem } = await import("../../lib/api/products");
      const result = await deleteItem(item.id, user?.uid || "");

      if (result.success) {
        toast.success("상품이 삭제되었습니다.");
        onClose();
        // 페이지 새로고침하여 목록 업데이트
        window.location.reload();
      } else {
        toast.error(result.error || "상품 삭제에 실패했습니다.");
      }
    } catch (error) {
      console.error("상품 삭제 실패:", error);
      toast.error("상품 삭제 중 오류가 발생했습니다.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBump = async () => {
    try {
      setIsBumping(true);
      const { updateItem } = await import("../../lib/api/products");
      const result = await updateItem(item.id, user?.uid || "", {
        updatedAt: new Date(),
      });

      if (result.success) {
        toast.success("상품이 끌어올려졌습니다!");
        onClose();
        // 페이지 새로고침하여 목록 업데이트
        window.location.reload();
      } else {
        toast.error(result.error || "끌어올리기에 실패했습니다.");
      }
    } catch (error) {
      console.error("끌어올리기 실패:", error);
      toast.error("끌어올리기 중 오류가 발생했습니다.");
    } finally {
      setIsBumping(false);
    }
  };

  const handleEditComplete = () => {
    setShowEditModal(false);
    onClose();
    // 페이지 새로고침하여 목록 업데이트
    window.location.reload();
  };

  if (!isOpen) return null;

  const conditionInfo = getConditionInfo((item as any).condition || item.condition || "A");
  const categoryInfo = getCategoryInfo(item.category);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            상품 상세 정보
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* 내용 */}
        <div className="p-6">
          <div className="space-y-6">
            {/* 이미지 갤러리 */}
            {item.images && item.images.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  상품 이미지
                </h3>
                <ItemGallery
                  images={item.images || []}
                  alt={
                    item.title ||
                    `${item.brand || ""} ${item.model || ""}`.trim() ||
                    "상품명 없음"
                  }
                  aiProcessedImages={item.aiProcessedImages || []}
                  maxHeight="300px"
                />
              </div>
            )}

            {/* 상품 정보 */}
            <div className="space-y-6">
              {/* 기본 정보 */}
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                      {item.title ||
                        `${item.brand || ""} ${item.model || ""}`.trim() ||
                        "상품명 없음"}
                    </h1>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {formatDate(item.createdAt)}
                      </span>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    {showEditActions && isSeller && (
                      <>
                        {item.status === "active" && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleBump}
                            disabled={isBumping}
                          >
                            <ArrowUp className="w-4 h-4 mr-1" />
                            끌어올리기
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={handleEdit}>
                          <Edit className="w-4 h-4 mr-1" />
                          수정
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDelete}
                          disabled={isDeleting}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          삭제
                        </Button>
                      </>
                    )}
                    <Button variant="outline" size="sm" onClick={handleShare}>
                      <Share2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="text-3xl font-bold text-blue-600 mb-4">
                  {formatPrice(item.price)}
                </div>

                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600">
                    {categoryInfo?.icon} {categoryInfo?.label}
                  </span>
                </div>
              </div>

              {/* 상세 정보 */}
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  상품 상세 정보
                </h3>
                <div className="space-y-4">
                  <table className="w-full">
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="py-3 px-4 text-sm font-medium text-gray-500 w-1/3">
                          카테고리
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-900">
                          {categoryInfo?.icon} {categoryInfo?.label}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-sm font-medium text-gray-500">
                          상품명
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-900">
                          {item.title ||
                            `${item.brand || ""} ${item.model || ""}`.trim() ||
                            "상품명 없음"}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-sm font-medium text-gray-500">
                          지역
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-900 flex items-center">
                          <MapPin className="w-4 h-4 mr-1" />
                          {item.region ? item.region.replace(/\s*>\s*/g, " ") : "지역정보 없음"}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-sm font-medium text-gray-500">
                          가격
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-900 font-semibold">
                          {formatPrice(item.price)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 상품 설명 */}
              {item.description && (
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    상품 설명
                  </h3>
                  <div className="prose prose-sm max-w-none">
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {item.description}
                    </p>
                  </div>
                </div>
              )}

              {/* 판매자 정보 */}
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <div
                  className="cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setShowSellerProfileModal(true)}
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    판매자 정보
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                        {sellerProfile?.photoURL ? (
                          <img
                            src={sellerProfile.photoURL}
                            alt={sellerProfile.nickname}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <User className="w-6 h-6 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {sellerProfile?.nickname || "판매자"}
                        </h3>
                        <div className="flex items-center space-x-2">
                          <div className="flex items-center text-sm text-gray-500">
                            <Star className="w-4 h-4 text-yellow-400 mr-1" />
                            {(() => {
                              const grade = sellerProfile?.grade || "C";
                              const gradeInfo = getGradeInfo(grade);
                              return gradeInfo.displayName;
                            })()}
                          </div>
                          {sellerProfile?.region && (
                            <div className="flex items-center text-sm text-gray-500">
                              <MapPin className="w-4 h-4 mr-1" />
                              {sellerProfile.region}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">거래 횟수</span>
                        <p className="font-semibold">
                          {sellerProfile?.tradesCount || 0}회
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">받은 후기</span>
                        <p className="font-semibold">
                          {sellerProfile?.reviewsCount || 0}개
                        </p>
                      </div>
                    </div>

                    {sellerProfile?.introShort && (
                      <div className="pt-2 border-t border-gray-200">
                        <p className="text-sm text-gray-600">
                          {sellerProfile.introShort}
                        </p>
                      </div>
                    )}

                    <div className="text-xs text-gray-400 text-center pt-2">
                      클릭하여 상세 프로필 보기
                    </div>
                  </div>
                </div>
              </div>

              {/* 배송 방법들 */}
              <div className="flex flex-wrap gap-2">
                <div className="flex flex-wrap gap-2">
                  {item.shippingTypes && item.shippingTypes.length > 0 ? (
                    item.shippingTypes
                      .filter(type => getShippingTypeLabel(type) !== null)
                      .map((type, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                        >
                          <Truck className="w-4 h-4 mr-1" />
                          {getShippingTypeLabel(type)}
                        </span>
                      ))
                  ) : (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      <Truck className="w-4 h-4 mr-1" />
                      {getShippingTypeLabel(item.shippingType || "direct")}
                    </span>
                  )}
                </div>
              </div>

              {/* 구매 CTA */}
              <PurchaseCTA
                status={
                  item.status as
                    | "active"
                    | "reserved"
                    | "paid_hold"
                    | "shipped"
                    | "sold"
                    | "escrow_completed"
                }
                escrowEnabled={item.escrowEnabled}
                isLoggedIn={!!user}
                itemId={item.id}
                sellerUid={item.sellerUid}
                buyerUid={item.buyerUid}
                currentUserId={user?.uid}
                onPurchase={async () => {
                  // 구매하기 클릭 시 상품 상태를 reserved로 변경하고 거래 관리 페이지로 이동
                  try {
                    const { updateItemStatus } = await import(
                      "../../lib/api/products"
                    );
                    const result = await updateItemStatus(
                      item.id,
                      "reserved",
                      user?.uid
                    );

                    if (result.success) {
                      // 거래 시작 메시지 전송
                      if (item.sellerUid && user?.uid) {
                        try {
                          const chatResult = await getOrCreateChat(
                            item.id,
                            user.uid, // 구매자 UID
                            item.sellerUid, // 판매자 UID
                            "거래가 시작되었습니다. 구매자와 소통을 시작해주세요."
                          );

                          if (chatResult.success) {
                            console.log(
                              "거래 시작 메시지 전송 완료:",
                              chatResult.chatId
                            );
                          }
                        } catch (error) {
                          console.error("거래 시작 메시지 전송 실패:", error);
                        }
                      }

                      // 거래 관리 페이지로 이동
                      window.location.href = `/transaction/${item.id}`;
                    } else {
                      console.error("상품 상태 변경 실패:", result.error);
                    }
                  } catch (error) {
                    console.error("구매 처리 실패:", error);
                  }
                }}
                onChat={async () => {
                  // 채팅 생성 후 모달 열기
                  if (user && item?.sellerUid && item?.id) {
                    try {
                      // 거래 상태에 따라 채팅 상대방 결정
                      let chatPartnerUid: string;
                      let firstMessage: string;
                      
                      if (item.sellerUid === user.uid) {
                        // 내가 판매자인 경우 → 구매자와 채팅
                        chatPartnerUid = item.buyerUid || "";
                        firstMessage = "거래 진행 상황을 안내드리겠습니다.";
                      } else {
                        // 내가 구매자인 경우 → 판매자와 채팅
                        chatPartnerUid = item.sellerUid;
                        firstMessage = "거래에 대해 문의드립니다.";
                      }

                      const chatResult = await getOrCreateChat({
                        itemId: item.id,
                        buyerUid: item.buyerUid || user.uid,
                        sellerUid: item.sellerUid,
                        firstMessage: firstMessage,
                      });

                      if (chatResult.success && chatResult.chatId) {
                        setSelectedChatId(chatResult.chatId);
                        setShowChatModal(true);
                      } else {
                        console.error("채팅 생성 실패:", chatResult.error);
                      }
                    } catch (error) {
                      console.error("채팅 생성 중 오류:", error);
                    }
                  }
                }}
                onLogisticsQuote={handleLogisticsQuote}
                onStartTransaction={async () => {
                  // 거래 진행하기 - 안전결제 완료 후 판매자가 거래를 시작
                  if (user && item?.sellerUid && item?.id && item?.buyerUid) {
                    try {
                      const { updateItemStatus } = await import(
                        "../../lib/api/products"
                      );
                      const result = await updateItemStatus(
                        item.id,
                        "reserved",
                        item.buyerUid
                      );

                      if (result.success) {
                        // 거래 시작 메시지 전송
                        try {
                          const chatResult = await getOrCreateChat({
                            itemId: item.id,
                            buyerUid: item.buyerUid,
                            sellerUid: item.sellerUid,
                            firstMessage: "✅ 거래가 시작되었습니다! 안전하게 거래를 진행해주세요.",
                          });

                          if (chatResult.success && chatResult.chatId) {
                            setSelectedChatId(chatResult.chatId);
                            setShowChatModal(true);
                          }
                        } catch (error) {
                          console.error("거래 시작 메시지 전송 실패:", error);
                        }

                        toast.success("거래가 시작되었습니다!");
                        // 페이지 새로고침하여 상태 업데이트
                        window.location.reload();
                      } else {
                        toast.error("거래 시작에 실패했습니다.");
                      }
                    } catch (error) {
                      console.error("거래 시작 실패:", error);
                      toast.error("거래 시작 중 오류가 발생했습니다.");
                    }
                  }
                }}
              />
            </div>
          </div>

          {/* 상태 타임라인 */}
          <div className="mt-6">
            <StatusTimeline
              status={
                item.status as
                  | "active"
                  | "reserved"
                  | "paid_hold"
                  | "shipped"
                  | "sold"
              }
              statusLog={[]}
              shippingInfo={item.shippingInfo}
              currentUserId={user?.uid}
              buyerUid={item.buyerUid}
            />
          </div>
        </div>
      </div>

      {/* 운송 견적 모달 */}
      <LogisticsQuoteModal
        isOpen={showLogisticsModal}
        onClose={() => setShowLogisticsModal(false)}
        itemId={item.id}
        buyerUid={user?.uid || ""}
        sellerUid={item.sellerUid}
        onQuoteSelect={handleLogisticsOrderCreate}
      />

      {/* 판매자 프로필 모달 */}
      <SellerProfileModal
        isOpen={showSellerProfileModal}
        onClose={() => setShowSellerProfileModal(false)}
        sellerProfile={sellerProfile}
      />

      {/* 채팅 모달 */}
      {showChatModal && selectedChatId && (
        <EnhancedChatModal
          isOpen={showChatModal}
          onClose={() => {
            console.log("채팅 모달 닫기 요청 - ItemDetailModal");
            setShowChatModal(false);
            setSelectedChatId(null);
          }}
          chatId={selectedChatId}
          onChatDeleted={() => {
            console.log("채팅 삭제됨 - 모달 닫기");
            setShowChatModal(false);
            setSelectedChatId(null);
          }}
        />
      )}

      {/* 상품 수정 모달 */}
      {showEditModal && (
        <EditProductModal
          productId={item.id}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSuccess={handleEditComplete}
        />
      )}
    </div>
  );
}
